import type {
  GamePhase,
  PlayerState,
  RoleId,
  VoteResult,
} from "@cloudnest/one-night-ultimate-werewolf-engine";
import { roleLabels } from "../labels";
import "../styles/one-night-ultimate-werewolf-tokens.css";
import "../styles/one-night-ultimate-werewolf-roster.css";

export type OneNightPhaseMode = "setup" | "night" | "day" | "vote" | "result";

export function phaseModeOf(phase: GamePhase): OneNightPhaseMode {
  switch (phase) {
    case "setup":
    case "role-peek":
      return "setup";
    case "night":
      return "night";
    case "day":
      return "day";
    case "vote":
      return "vote";
    case "reveal":
    case "complete":
      return "result";
  }
}

interface ProjectedCardLike {
  readonly cardId: string;
  readonly roleId: RoleId | null;
  readonly visibility: "hidden" | "known" | "revealed";
}

type RevealedCardLocationLike =
  | {
      readonly kind: "player";
      readonly playerId: string;
      readonly cardId: string;
    }
  | {
      readonly kind: "center";
      readonly slotIndex: 0 | 1 | 2;
      readonly cardId: string;
    };

/**
 * Structural view contract shared by the engine's ProjectedViewerState and the
 * server-sent OneNightViewerState — the board renders either.
 */
export interface OneNightRosterView {
  readonly phase: GamePhase;
  readonly players: readonly PlayerState[];
  readonly seatOrder: readonly string[];
  readonly centerSlots: readonly [string | null, string | null, string | null];
  readonly viewerPlayerId: string | null;
  readonly viewerCurrentCardId: string | null;
  readonly visibleCards: Readonly<Record<string, ProjectedCardLike>>;
  readonly revealedCardLocations: readonly RevealedCardLocationLike[];
  readonly voteResult: VoteResult | null;
}

export interface RosterTileInteraction {
  readonly selectablePlayerIds: readonly string[];
  readonly selectedPlayerIds: readonly string[];
  readonly onSelectPlayer: (playerId: string) => void;
}

export interface CenterCardInteraction {
  readonly selectableSlots: readonly (0 | 1 | 2)[];
  readonly selectedSlots: readonly (0 | 1 | 2)[];
  readonly onSelectSlot: (slotIndex: 0 | 1 | 2) => void;
}

interface OneNightRosterBoardProps {
  readonly view: OneNightRosterView;
  readonly highlightPlayerId?: string | null;
  readonly tileInteraction?: RosterTileInteraction;
  readonly centerInteraction?: CenterCardInteraction;
}

interface TileCard {
  readonly label: string;
  readonly dataCard: RoleId | "hidden";
}

function tileCardFor(view: OneNightRosterView, playerId: string): TileCard {
  const revealed = view.revealedCardLocations.find(
    (location) => location.kind === "player" && location.playerId === playerId,
  );
  const player = view.players.find((candidate) => candidate.id === playerId);
  // Own tile shows the card CURRENTLY at my seat (unknown after a swap);
  // other tiles show night knowledge at its original seat (startingCardId).
  const candidateCardIds =
    playerId === view.viewerPlayerId
      ? [revealed?.cardId ?? null, view.viewerCurrentCardId]
      : [revealed?.cardId ?? null, player?.startingCardId ?? null];

  for (const cardId of candidateCardIds) {
    if (!cardId) continue;
    const projected = view.visibleCards[cardId];
    if (projected?.roleId) {
      return {
        label: roleLabels.get(projected.roleId) ?? projected.roleId,
        dataCard: projected.roleId,
      };
    }
  }

  return { label: "뒷면", dataCard: "hidden" };
}

function centerCardFor(
  view: OneNightRosterView,
  slotIndex: 0 | 1 | 2,
): TileCard {
  const cardId = view.centerSlots[slotIndex];
  if (cardId) {
    const projected = view.visibleCards[cardId];
    if (projected?.roleId) {
      return {
        label: roleLabels.get(projected.roleId) ?? projected.roleId,
        dataCard: projected.roleId,
      };
    }
  }
  return { label: "?", dataCard: "hidden" };
}

const centerSlotIndexes = [0, 1, 2] as const;

export function OneNightRosterBoard({
  view,
  highlightPlayerId = null,
  tileInteraction,
  centerInteraction,
}: OneNightRosterBoardProps) {
  const phaseMode = phaseModeOf(view.phase);
  const playerById = new Map(view.players.map((player) => [player.id, player]));
  const orderedPlayerIds =
    view.seatOrder.length > 0
      ? view.seatOrder
      : view.players.map((player) => player.id);
  const voteCounts = new Map(
    (view.voteResult?.voteCounts ?? []).map((voteCount) => [
      voteCount.playerId,
      voteCount,
    ]),
  );
  const anyCenterFace = centerSlotIndexes.some(
    (slotIndex) => centerCardFor(view, slotIndex).dataCard !== "hidden",
  );

  return (
    <div
      className="onuw-roster-layout"
      data-testid="one-night-ultimate-werewolf-board"
      data-phase-mode={phaseMode}
    >
      <div
        className="onuw-center-strip"
        data-testid="one-night-ultimate-werewolf-center-strip"
      >
        <div className="onuw-center-caption">
          <span>중앙 3장</span>
          <strong>{anyCenterFace ? "공개" : "비공개"}</strong>
        </div>
        {centerSlotIndexes.map((slotIndex) => {
          const card = centerCardFor(view, slotIndex);
          const selectable =
            centerInteraction?.selectableSlots.includes(slotIndex) ?? false;
          const selected =
            centerInteraction?.selectedSlots.includes(slotIndex) ?? false;
          const classes = [
            "onuw-center-card",
            card.dataCard !== "hidden" ? "is-front" : "",
            selectable ? "is-allowed" : "",
            selected ? "is-selected" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const content = (
            <>
              <span className="onuw-center-slot-label">
                중앙 {slotIndex + 1}
              </span>
              {card.label}
            </>
          );
          if (selectable && centerInteraction) {
            return (
              <button
                type="button"
                className={classes}
                data-testid="one-night-ultimate-werewolf-center-card"
                data-slot={slotIndex}
                data-card={card.dataCard}
                data-selectable="true"
                data-selected={selected ? "true" : "false"}
                aria-pressed={selected}
                key={slotIndex}
                onClick={() => centerInteraction.onSelectSlot(slotIndex)}
              >
                {content}
              </button>
            );
          }
          return (
            <div
              className={classes}
              data-testid="one-night-ultimate-werewolf-center-card"
              data-slot={slotIndex}
              data-card={card.dataCard}
              data-selectable="false"
              key={slotIndex}
            >
              {content}
            </div>
          );
        })}
      </div>

      <div
        className="onuw-roster-grid"
        data-testid="one-night-ultimate-werewolf-roster-grid"
      >
        {orderedPlayerIds.map((playerId) => {
          const player = playerById.get(playerId);
          if (!player) return null;
          const card = tileCardFor(view, playerId);
          const voteCount = voteCounts.get(playerId);
          const selectable =
            tileInteraction?.selectablePlayerIds.includes(playerId) ?? false;
          const selected =
            tileInteraction?.selectedPlayerIds.includes(playerId) ?? false;
          const tileClasses = [
            "onuw-roster-tile",
            playerId === highlightPlayerId ? "is-viewer" : "",
            selectable ? "is-selectable" : "",
            selected ? "is-selected" : "",
            voteCount?.isTop ? "is-target-active" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const content = (
            <>
              <span className="onuw-seat-no">{player.seatNumber}</span>
              <span className="onuw-tile-name">{player.name}</span>
              <span className="onuw-tile-card" data-card={card.dataCard}>
                {card.label}
              </span>
              <span className="onuw-tile-badge">
                {voteCount && view.voteResult ? (
                  <span className="onuw-vote-count">{voteCount.count}</span>
                ) : null}
              </span>
            </>
          );
          if (selectable && tileInteraction) {
            return (
              <button
                type="button"
                className={tileClasses}
                data-testid="one-night-ultimate-werewolf-roster-tile"
                data-player-id={playerId}
                data-seat={player.seatNumber}
                data-card={card.dataCard}
                data-vote-count={voteCount?.count ?? 0}
                data-selectable="true"
                data-selected={selected ? "true" : "false"}
                aria-pressed={selected}
                key={playerId}
                onClick={() => tileInteraction.onSelectPlayer(playerId)}
              >
                {content}
              </button>
            );
          }
          return (
            <div
              className={tileClasses}
              data-testid="one-night-ultimate-werewolf-roster-tile"
              data-player-id={playerId}
              data-seat={player.seatNumber}
              data-card={card.dataCard}
              data-vote-count={voteCount?.count ?? 0}
              data-selectable="false"
              key={playerId}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
